import { useEffect, useState } from "react";
import { FaStar } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { LuSend } from "react-icons/lu";
import { RxCross1 } from "react-icons/rx";
import { LIVE_URL } from "../../../../config/config";
import useAuthToken from "../../../../hooks/useAuthToken";
import Spinner from "../../spinner/Spinner";
import styles from "./css/feedback.module.scss";

const FeedbackModel = ({ feedback, onClose }) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState("");
  const [feedbackMessages, setFeedbackMessages] = useState([]);
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { token } = useAuthToken();

  const resetForm = () => {
    setSelectedMessage("");
  };

  // Fetch feedback list for the dropdown
  useEffect(() => {
    const fetchFeedbackList = async () => {
      try {
        const response = await fetch(
          `${LIVE_URL}/admin/list/feedback-messages`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const data = await response.json();
        if (response.ok) {
          setFeedbackMessages(data.data.replies_messages || []);
        }
      } catch (error) {
        console.error("Error fetching feedback list:", error);
      }
    };
    fetchFeedbackList();
  }, [token]);

  // Handle popup message display and auto-hide
  useEffect(() => {
    let timer;
    if (popupMessage) {
      const popupElement = document.getElementById("popup-message");
      if (popupElement) {
        popupElement.style.opacity = "1";
      }
      timer = setTimeout(() => {
        if (popupElement) {
          popupElement.style.opacity = "0";
        }
        setTimeout(() => {
          setPopupMessage("");
        }, 500);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [popupMessage]);

  // Handle sending feedback reply
  const handleSendReply = async () => {
    if (!selectedMessage) {
      setPopupMessage("Please select a message");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${LIVE_URL}/admin/reply/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          feedback_id: feedback?.id,
          email: feedback?.feedback_user?.email,
          reply: selectedMessage,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setPopupMessage("Reply sent successfully");
        setTimeout(() => {
          onClose();
        }, 2000);
        resetForm();
      } else {
        setPopupMessage(data.message || "Failed to send reply");
      }
    } catch (error) {
      console.error("Send Reply Error:", error);
      setPopupMessage("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!feedback) return null;

  const colors = ["#e94057", "#4F46E5", "#10B981", "#F59E0B", "#8B5CF6"];

  const firstLetter =
    feedback.feedback_user?.firstname?.charAt(0)?.toUpperCase() || "?";

  const bgColor = colors[firstLetter.charCodeAt(0) % colors.length];

  return (
    <>
      <div
        id="popup-message"
        className={`${styles.update_message} ${popupMessage.includes(
          "success",
        )}`}
      >
        {popupMessage && <p>{popupMessage}</p>}
      </div>
      <div
        className={styles.popupOverlay}
        onClick={!loading ? onClose : undefined}
      >
        <div className={styles.popupBox} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2>Feedback Response</h2>

            <RxCross1
              className={styles.closeButton}
              onClick={!loading ? onClose : undefined}
            />
          </div>
          <hr />

          <div className={styles.userSection}>
            <div
              className={styles.userImage}
              style={{ backgroundColor: bgColor }}
            >
              {firstLetter}
            </div>

            <div>
              <h3>{feedback.feedback_user?.firstname}</h3>

              <div className={styles.rating}>
                {Array.from({ length: feedback.ratting }).map((_, index) => (
                  <FaStar key={index} color="#e94057" />
                ))}
              </div>
            </div>
          </div>

          <div className={styles.description}>{feedback.description}</div>

          {loading ? (
            <div className={styles.spinnerWrapper}>
              <Spinner />
            </div>
          ) : (
            <>
              <div className={styles.dropdown}>
                <div
                  className={styles.dropdownHeader}
                  onClick={() => setOpenDropdown(!openDropdown)}
                >
                  <span>{selectedMessage || "Select Message"}</span>
                  <IoIosArrowDown
                    className={`${styles.arrow} ${openDropdown ? styles.rotate : ""}`}
                  />
                </div>

                {openDropdown && (
                  <div className={styles.dropdownList}>
                    {feedbackMessages.map((item, index) => (
                      <div
                        key={index}
                        className={styles.dropdownItem}
                        onClick={() => {
                          setSelectedMessage(item.message);
                          setOpenDropdown(false);
                        }}
                      >
                        {item.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                className={styles.sendButton}
                onClick={handleSendReply}
                disabled={loading} // ✅ prevent double clicks
              >
                <LuSend />
                Send
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedbackModel;
